import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useInventory } from '../hooks/useInventory';
import { useCreateTrade } from '../hooks/useTrades';
import { useAuth } from '../context/AuthContext';
import { StickerCard, teamColor } from '../components/sticker/StickerCard';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import api from '../lib/api';
import type { InventoryItem, SearchResult, UserProfile } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type Tab = 'OFREZCO' | 'BUSCO' | 'COMUNIDAD';

interface CommunityItem {
  userId: string;
  username: string;
  stickerId: string;
  playerName?: string;
  nationalTeam?: string;
  status: string;
  quantity: number;
  ciudad?: string;
  ratingAvg?: number;
  ratingCount?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini rating display
// ─────────────────────────────────────────────────────────────────────────────
function MiniRating({ avg, count }: { avg?: number; count?: number }) {
  if (!avg || !count) return <span className="text-xs text-gray-300 font-semibold">Sin calificaciones</span>;
  const stars = Math.round(avg);
  return (
    <span className="flex items-center gap-1">
      {[1,2,3,4,5].map((s) => (
        <span key={s} className={`text-[10px] ${s <= stars ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
      ))}
      <span className="text-xs text-gray-400 font-semibold">{avg.toFixed(1)} ({count})</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trade proposal modal
// ─────────────────────────────────────────────────────────────────────────────
function ProposeTradeModal({
  targetUserId,
  targetUsername,
  requestedStickerId,
  myDuplicates,
  onClose,
}: {
  targetUserId: string;
  targetUsername: string;
  requestedStickerId: string;
  myDuplicates: InventoryItem[];
  onClose: () => void;
}) {
  const createTrade = useCreateTrade();
  const [error, setError] = useState('');

  // Fetch target's public profile to know which stickers they still need (FALTA)
  const { data: targetProfile, isLoading: loadingTarget } = useQuery<{ wants?: string[] }>({
    queryKey: ['publicProfile', targetUsername],
    queryFn: async () => {
      const { data } = await api.get(`/users/${encodeURIComponent(targetUsername)}`);
      return data.data;
    },
    staleTime: 60_000,
  });

  // Only offer duplicates that the target actually needs (FALTA for them)
  const eligibleOffers = targetProfile?.wants
    ? myDuplicates.filter((d) => targetProfile.wants!.includes(d.stickerId))
    : myDuplicates; // while loading, show all (backend will validate anyway)

  const [offeredStickerId, setOfferedStickerId] = useState('');

  // Update selected offer when eligible list changes
  const firstEligible = eligibleOffers[0]?.stickerId ?? '';
  const effectiveOffer = offeredStickerId && eligibleOffers.some(d => d.stickerId === offeredStickerId)
    ? offeredStickerId
    : firstEligible;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    createTrade.mutate(
      { targetUserId, offeredStickers: [effectiveOffer], requestedStickers: [requestedStickerId] },
      {
        onSuccess: onClose,
        onError: () => setError('Error al proponer el intercambio. Intenta de nuevo.'),
      }
    );
  }

  const noOffers = !loadingTarget && eligibleOffers.length === 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">Proponer intercambio</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>
        </div>

        <div className="bg-brand-50 rounded-xl px-4 py-3 mb-4 text-sm">
          <p className="text-gray-500 font-semibold">
            A: <span className="text-brand-600 font-bold">{targetUsername}</span>
          </p>
          <p className="text-gray-500 font-semibold mt-1">
            Pides: <span className="text-night font-bold">#{requestedStickerId}</span>
          </p>
          {targetProfile?.wants && (
            <p className="text-gray-400 text-xs mt-1">
              {targetUsername} necesita {targetProfile.wants.length} figuritas
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
              Figurita que ofreces a cambio
            </label>
            {loadingTarget ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <Spinner size={14} /> Verificando compatibilidad...
              </div>
            ) : noOffers ? (
              <p className="text-sm text-amber-600 font-semibold bg-amber-50 rounded-lg px-3 py-2">
                Ninguna de tus duplicadas es necesaria para {targetUsername}.
              </p>
            ) : (
              <select
                value={effectiveOffer}
                onChange={(e) => setOfferedStickerId(e.target.value)}
                className="input-field"
                required
              >
                {eligibleOffers.map((d) => (
                  <option key={d.stickerId} value={d.stickerId}>
                    #{d.albumNumber} — {d.playerName || d.stickerId} ({d.nationalTeam})
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 font-semibold">{error}</p>
          )}

          <div className="flex gap-2 mt-1">
            <Button
              type="submit"
              loading={createTrade.isPending}
              disabled={noOffers || loadingTarget}
            >
              Enviar propuesta 🤝
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Busco" — faltantes del usuario + quién las tiene
// ─────────────────────────────────────────────────────────────────────────────
function BuscoTab({
  faltantes,
  myDuplicates,
}: {
  faltantes: InventoryItem[];
  myDuplicates: InventoryItem[];
}) {
  const [selected, setSelected] = useState<InventoryItem | null>(null);
  const [tradeTarget, setTradeTarget] = useState<{ userId: string; username: string; stickerId: string } | null>(null);
  const [teamFilter, setTeamFilter] = useState<string | undefined>();

  const teams = [...new Set(faltantes.map((i) => i.nationalTeam))].sort();

  const filtered = teamFilter
    ? faltantes.filter((i) => i.nationalTeam === teamFilter)
    : faltantes;

  // Search who has the selected sticker as DUPLICADA
  const { data: owners, isLoading: loadingOwners } = useQuery<SearchResult[]>({
    queryKey: ['search', selected?.stickerId, 'DUPLICADA'],
    queryFn: async () => {
      const { data } = await api.get('/search', {
        params: { stickerId: selected!.stickerId, status: 'DUPLICADA' },
      });
      return data.data?.users ?? [];
    },
    enabled: !!selected,
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Team filter */}
      {teams.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setTeamFilter(undefined)}
            className={[
              'px-3 py-1.5 rounded-full text-xs font-bold border transition-all',
              !teamFilter ? 'bg-night text-white border-night' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400',
            ].join(' ')}
          >
            Todas
          </button>
          {teams.map((team) => {
            const active = teamFilter === team;
            const color = teamColor(team);
            return (
              <button
                key={team}
                onClick={() => setTeamFilter(team)}
                className={[
                  'px-3 py-1.5 rounded-full text-xs font-bold border transition-all',
                  active ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
                ].join(' ')}
                style={active ? { backgroundColor: color, borderColor: color } : {}}
              >
                {team}
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">🏆</div>
          <p className="font-bold text-gray-500">¡Tu álbum está completo!</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 font-semibold">
            Toca una figurita para ver quién la tiene disponible para intercambiar
          </p>
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
            {filtered.map((item) => (
              <button
                key={item.stickerId}
                onClick={() => setSelected(selected?.stickerId === item.stickerId ? null : item)}
                className={[
                  'rounded-xl border-2 transition-all text-left',
                  selected?.stickerId === item.stickerId
                    ? 'border-brand-500 shadow-md scale-[1.02]'
                    : 'border-transparent',
                ].join(' ')}
              >
                <StickerCard item={item} />
              </button>
            ))}
          </div>
        </>
      )}

      {/* Who has it panel */}
      {selected && (
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800">
              ¿Quién tiene #{selected.albumNumber} — {selected.playerName || selected.stickerId}?
            </h3>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
          </div>

          {loadingOwners ? (
            <div className="flex justify-center py-4"><Spinner size={24} /></div>
          ) : owners && owners.length > 0 ? (
            <div className="flex flex-col gap-2">
              {owners.map((owner) => (
                <div
                  key={owner.userId}
                  className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5"
                >
                  <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-600 font-bold text-sm flex items-center justify-center flex-shrink-0 uppercase">
                    {owner.username.slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-700">{owner.username}{owner.ciudad ? <span className="text-gray-400 font-normal"> · {owner.ciudad}</span> : null}</p>
                    <MiniRating avg={owner.ratingAvg} count={owner.ratingCount} />
                  </div>
                  <button
                    onClick={() => setTradeTarget({ userId: owner.userId, username: owner.username, stickerId: selected.stickerId })}
                    className="px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-bold hover:bg-brand-600 transition-colors flex-shrink-0"
                  >
                    🤝 Intercambiar
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 font-semibold text-center py-4">
              Nadie tiene esta figurita disponible para intercambiar aún
            </p>
          )}
        </div>
      )}

      {/* Trade modal */}
      {tradeTarget && (
        <ProposeTradeModal
          targetUserId={tradeTarget.userId}
          targetUsername={tradeTarget.username}
          requestedStickerId={tradeTarget.stickerId}
          myDuplicates={myDuplicates}
          onClose={() => setTradeTarget(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Comunidad" — duplicadas públicas de otros usuarios
// ─────────────────────────────────────────────────────────────────────────────
function ComunidadTab({ myDuplicates, myUserId, catalog }: { myDuplicates: InventoryItem[]; myUserId: string; catalog: InventoryItem[] }) {
  const catalogById = Object.fromEntries(catalog.map((i) => [i.stickerId, i]));
  const [tradeTarget, setTradeTarget] = useState<{ userId: string; username: string; stickerId: string } | null>(null);
  const [soloMiPais, setSoloMiPais] = useState(true);

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get('/profile');
      return data.data;
    },
    retry: false,
  });
  const hasPais = Boolean(profile?.pais);

  // Fetch all public DUPLICADA stickers from other users
  const { data: community, isLoading } = useQuery<CommunityItem[]>({
    queryKey: ['community-duplicates', soloMiPais, hasPais],
    queryFn: async () => {
      const params: Record<string, string> = { status: 'DUPLICADA' };
      if (!soloMiPais || !hasPais) params['allCountries'] = 'true';
      const { data } = await api.get('/search', { params });
      return (data.data?.users ?? []) as CommunityItem[];
    },
  });

  const others = (community ?? []).filter((i) => i.userId !== myUserId);

  // Group by user
  const byUser = others.reduce<Record<string, CommunityItem[]>>((acc, item) => {
    if (!acc[item.userId]) acc[item.userId] = [];
    acc[item.userId].push(item);
    return acc;
  }, {});

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size={32} /></div>;
  }

  if (others.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <div className="text-5xl mb-3">🌍</div>
        <p className="font-bold text-gray-500 mb-1">Aún no hay figuritas disponibles</p>
        <p className="text-xs">Cuando otros usuarios marquen sus repetidas aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Filtro por país */}
      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
        <span className="text-base">🌎</span>
        <div className="flex-1">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1.5">Ver figuritas de</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSoloMiPais(true)}
              disabled={!hasPais}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                soloMiPais && hasPais
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
              title={!hasPais ? 'Configura tu país en el perfil primero' : undefined}
            >
              Solo mi país
            </button>
            <button
              type="button"
              onClick={() => setSoloMiPais(false)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                !soloMiPais || !hasPais
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-emerald-300'
              }`}
            >
              Todos los países
            </button>
          </div>
          {!hasPais && (
            <p className="text-xs text-amber-600 font-semibold mt-1.5">
              Configura tu país en el perfil para filtrar por región
            </p>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 font-semibold">
        {others.length} figurita{others.length > 1 ? 's' : ''} disponible{others.length > 1 ? 's' : ''} de {Object.keys(byUser).length} usuario{Object.keys(byUser).length > 1 ? 's' : ''}
      </p>

      {Object.entries(byUser).map(([, items]) => {
        const username = items[0].username;
        const userId = items[0].userId;
        return (
          <div key={userId} className="bg-white rounded-2xl border border-gray-100 shadow-card p-4">
            {/* User header */}
            <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-100">
              <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-600 font-bold text-sm flex items-center justify-center flex-shrink-0 uppercase">
                {username.slice(0, 2)}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">{username}{items[0].ciudad ? <span className="text-gray-400 font-normal text-sm"> · {items[0].ciudad}</span> : null}</p>
                <MiniRating avg={items[0].ratingAvg} count={items[0].ratingCount} />
                <p className="text-xs text-gray-400 mt-0.5">{items.length} figurita{items.length > 1 ? 's' : ''} disponible{items.length > 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Sticker list */}
            <div className="flex flex-col gap-1.5">
              {items.map((item) => {
                const meta = catalogById[item.stickerId];
                return (
                <div key={item.stickerId} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-gray-50 transition-colors">
                  <span className="text-xs font-bold text-gray-400 w-8 flex-shrink-0">#{meta?.albumNumber ?? item.stickerId}</span>
                  <span className="flex-1 text-sm font-semibold text-gray-700 truncate">
                    {meta?.playerName ?? item.stickerId}
                    {(meta?.nationalTeam ?? item.nationalTeam) && (
                      <span className="text-xs text-gray-400 font-normal ml-1">· {meta?.nationalTeam ?? item.nationalTeam}</span>
                    )}
                  </span>
                  <span className="text-xs text-amber-600 font-bold">{item.quantity}×</span>
                  <button
                    onClick={() => setTradeTarget({ userId, username, stickerId: item.stickerId })}
                    className="px-2.5 py-1 bg-brand-500 text-white rounded-lg text-xs font-bold hover:bg-brand-600 transition-colors flex-shrink-0"
                  >
                    🤝
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {tradeTarget && (
        <ProposeTradeModal
          targetUserId={tradeTarget.userId}
          targetUsername={tradeTarget.username}
          requestedStickerId={tradeTarget.stickerId}
          myDuplicates={myDuplicates}
          onClose={() => setTradeTarget(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function CatalogPage() {
  const [tab, setTab] = useState<Tab>('OFREZCO');
  const [selectedTeam, setSelectedTeam] = useState<string | undefined>();
  const [tradeTarget, setTradeTarget] = useState<{ userId: string; username: string; stickerId: string } | null>(null);
  const { data: inventory, isLoading } = useInventory();
  const { user } = useAuth();

  const duplicadas = (inventory ?? []).filter((i) => i.status === 'DUPLICADA');
  const faltantes  = (inventory ?? []).filter((i) => i.status === 'FALTA');

  const teamsWithDuplicates = [...new Set(duplicadas.map((i) => i.nationalTeam))].sort();

  const filteredDuplicadas = selectedTeam
    ? duplicadas.filter((i) => i.nationalTeam === selectedTeam)
    : duplicadas;

  if (isLoading) {
    return <div className="flex justify-center items-center py-20"><Spinner size={40} /></div>;
  }

  const TAB_CONFIG: Record<Tab, { label: string; emoji: string; count: number; activeClass: string }> = {
    OFREZCO:   { label: 'Ofrezco',   emoji: '🤝', count: duplicadas.length, activeClass: 'bg-amber-400 text-white border-amber-400' },
    BUSCO:     { label: 'Busco',     emoji: '🔍', count: faltantes.length,  activeClass: 'bg-brand-500 text-white border-brand-500' },
    COMUNIDAD: { label: 'Comunidad', emoji: '🌍', count: 0,                 activeClass: 'bg-emerald-500 text-white border-emerald-500' },
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
        <h1 className="text-2xl font-display text-night mb-0.5" style={{ fontFamily: '"Fredoka One", cursive' }}>
          Marketplace
        </h1>
        <p className="text-sm text-gray-400 font-semibold">
          Intercambia figuritas con otros coleccionistas
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {(['OFREZCO', 'BUSCO', 'COMUNIDAD'] as Tab[]).map((t) => {
          const c = TAB_CONFIG[t];
          const active = tab === t;
          return (
            <button
              key={t}
              onClick={() => { setTab(t); setSelectedTeam(undefined); }}
              className={[
                'flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl border font-bold text-sm transition-all',
                active ? c.activeClass + ' shadow-md' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300',
              ].join(' ')}
            >
              <span>{c.emoji}</span>
              <span>{c.label}</span>
              {t !== 'COMUNIDAD' && (
                <span className={[
                  'text-xs px-1.5 py-0.5 rounded-full font-bold',
                  active ? 'bg-white/30 text-white' : 'bg-gray-100 text-gray-500',
                ].join(' ')}>
                  {c.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── OFREZCO tab ── */}
      {tab === 'OFREZCO' && (
        <>
          {/* Photo tip */}
          <div className="bg-brand-50 border border-brand-100 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-xl flex-shrink-0">📷</span>
            <p className="text-xs text-brand-700 font-semibold leading-relaxed">
              Toca el ícono de cámara en cada figurita para fotografiarla.
              La foto se usará como referencia en los intercambios.
            </p>
          </div>

          {/* Team filter */}
          {teamsWithDuplicates.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedTeam(undefined)}
                className={[
                  'px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all',
                  !selectedTeam ? 'bg-night text-white border-night' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400',
                ].join(' ')}
              >
                Todas
              </button>
              {teamsWithDuplicates.map((team) => {
                const active = selectedTeam === team;
                const color = teamColor(team);
                return (
                  <button
                    key={team}
                    onClick={() => setSelectedTeam(team)}
                    className={[
                      'px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all',
                      active ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400',
                    ].join(' ')}
                    style={active ? { backgroundColor: color, borderColor: color } : {}}
                  >
                    {team}
                  </button>
                );
              })}
            </div>
          )}

          {filteredDuplicadas.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">🤝</div>
              <p className="font-bold text-gray-500 mb-1">No tienes figuritas duplicadas todavía</p>
              <p className="text-xs">Cuando tengas más de 1 de una figurita aparecerá aquí</p>
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
              {filteredDuplicadas.map((item) => (
                <StickerCard key={item.stickerId} item={item} allowPhoto />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── BUSCO tab ── */}
      {tab === 'BUSCO' && (
        <BuscoTab faltantes={faltantes} myDuplicates={duplicadas} />
      )}

      {/* ── COMUNIDAD tab ── */}
      {tab === 'COMUNIDAD' && (
        <ComunidadTab myDuplicates={duplicadas} myUserId={user?.userId ?? ''} catalog={inventory ?? []} />
      )}

      {/* Trade modal (from Ofrezco) */}
      {tradeTarget && (
        <ProposeTradeModal
          targetUserId={tradeTarget.userId}
          targetUsername={tradeTarget.username}
          requestedStickerId={tradeTarget.stickerId}
          myDuplicates={duplicadas}
          onClose={() => setTradeTarget(null)}
        />
      )}
    </div>
  );
}
